import { IconTrash } from '@developerz/ui';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import type { TokenRenameController } from '../lib/tokenRename';
import type { TokenRevokeController } from '../lib/tokenRevoke';
import type { MintInput } from '../lib/tokens';
import { ConfirmDialog } from './ConfirmDialog';
import { MintTokenModal } from './MintTokenModal';
import { RenameTokenModal } from './RenameTokenModal';
import { Toast } from './Toast';

// Everything the tokens page can put OVER itself: the mint, the relabel, the
// revoke confirm and the revoke's failure notice. Presenter — `<AccountTokens/>`
// keeps the query and the mutation orchestration, which is the one reason it
// changes; this file changes when a dialog does.
//
// The two write controllers ride in whole rather than as unpacked props: each is
// already a typed interface (`createTokenRename` / `createTokenRevoke`) and
// spreading them here would be a second, drifting statement of the same shape.

export type AccountTokenDialogsProps = {
  readonly mint: {
    readonly open: boolean;
    readonly mintable: readonly string[];
    readonly pending: boolean;
    readonly error: string | null;
    readonly onMint: (input: MintInput) => void;
    readonly onClose: () => void;
  };
  readonly rename: TokenRenameController;
  readonly revoke: TokenRevokeController;
};

export function AccountTokenDialogs(props: AccountTokenDialogsProps): JSX.Element {
  return (
    <>
      <MintTokenModal
        open={props.mint.open}
        mintable={props.mint.mintable}
        pending={props.mint.pending}
        error={props.mint.error}
        onMint={props.mint.onMint}
        onClose={props.mint.onClose}
      />
      <RenameTokenModal
        token={props.rename.target()}
        pending={props.rename.pending()}
        error={props.rename.error()}
        onRename={props.rename.save}
        onClose={props.rename.close}
      />
      <ConfirmDialog
        open={props.revoke.target() !== null}
        title={t('account_tokens.revoke_title')}
        desc={t('account_tokens.revoke_body', {
          // The NAME, not the prefix: every token's prefix is the same literal,
          // so the old copy asked "revoke the dev_pat_… token?" of a reader
          // holding four of them. An unnamed row still reads as itself.
          name: props.revoke.target()?.name ?? t('account_tokens.unnamed'),
        })}
        media={<IconTrash />}
        cancelLabel={t('account_tokens.cancel')}
        actionLabel={t('account_tokens.revoke')}
        destructive
        onCancel={props.revoke.close}
        onConfirm={props.revoke.confirm}
      />
      <Toast message={props.revoke.error()} tone="error" onDismiss={props.revoke.clearError} />
    </>
  );
}
